import { classMap, customElement, html, PropertyValues } from '@adobe/edex/ui/edex-lit';
import {
  AdobeAnalyticsEvent,
  CCX_POST_REMIX_DIRECT_LINK,
  CCX_PROJECT_LINK_ANALYTICS_IDENTIFIER,
  COPY_PROJECT_TIMEOUT,
  EdexDomID,
  EdexTagName,
  QueryParam,
  RESOURCE_COMPONENT_LABELS,
  SURVEY_CONTENT_ACQUIRED_COOKIE_KEY,
} from '@adobe/edex/ui/shared/constants';
import {
  CarouselItem,
  DialogState,
  DownloadFileDialogPayload,
  PreviewCCXAssetType,
  PreviewClickTrackingMeta,
  PreviewType,
} from '@adobe/edex/ui/types';
import { I18nKey } from '@adobe/edex/ui/shared/constants/i18n';
import { $t } from '@adobe/edex/ui/shared/config/i18n';
import '@adobe/edex/ui/components/shared/file-thumbnail/file-thumbnail';
import {
  fetchProfileStorage,
  getAssetURL,
  isCCXRegularTemplateType,
  isCCXTemplateOrProjectUrl,
  isEntitled,
  setSurveyCookieForNoCAParams,
  trackTargetEvent,
} from '@adobe/edex/ui/shared/utils';
import { ResourceHeaderToolbar } from '@adobe/edex/ui/components/resource/resource-header-toolbar/resource-header-toolbar';
import '@spectrum-web-components/icons-workflow/icons/sp-icon-chevron-right';
import '@spectrum-web-components/icons-workflow/icons/sp-icon-chevron-left';
import '@spectrum-web-components/icons-workflow/icons/sp-icon-play-circle';
import '@spectrum-web-components/icons-workflow/icons/sp-icon-link';

import { isEmpty, max, min } from 'lodash-es';
import {
  NX_CCX_HOST,
  NX_CDN_HOST,
  NX_ENABLE_PROJECT_X_ASSET_BROWSER,
  NX_PROJECTX_ASSET_BROWSER_URL,
  NX_RAPI_FLAG,
} from '@env/environment';
import { Analytics } from '@adobe/edex/ui/shared/config/analytics';
import { AnalyticsEvent } from '@adobe/edex/ui/constants';
import { FullStoryUtils } from '@adobe/edex/ui/shared/config/fullstory';
import { resourceAPI } from '@adobe/edex/ui/api';
import { getQueryParam } from '@adobe/edex/ui/shared/utils/routes';
import { getNavSource } from '@adobe/edex/ui/shared/utils';
import '@adobe/edex/ui/components/shared/dialog/dialog';

import(
  /* webpackChunkName: "component/file-preview-element-styles" */
  /* webpackMode: "lazy" */
  '@adobe/edex/ui/components/resource/attachment-preview/attachment-preview.scss'
);

enum ChevronDirection {
  left = 'left',
  right = 'right',
}

const PAGE_URL = `${NX_PROJECTX_ASSET_BROWSER_URL}/webpage`;

@customElement(EdexTagName.attachmentPreview)
export class AttachmentPreview extends ResourceHeaderToolbar {
  get useLightDom() {
    return true;
  }

  static get properties() {
    return {
      ...super.properties,
      attachments: {
        type: Array,
      },
      selectedIndex: { attribute: false },
      isEntitledToCCX: { type: Boolean },
      resourceID: { type: String },
      previewWidth: { type: Number },
      dialog: { attribute: false },
    };
  }
  attachments: CarouselItem[];
  selectedIndex = parseInt(getQueryParam(QueryParam.previewItem), 10) || 0;
  isEntitledToCCX = true;
  entitlementFirstUpdated = false;
  userId: string;
  resourceID: string;
  previewWidth: number;

  get selectedItem() {
    return this.attachments?.[this.selectedIndex];
  }

  get url() {
    return this.selectedItem?.url;
  }

  get showActionButton(): boolean {
    const { selfIsFulfilled, selectedItem } = this;
    if (!selfIsFulfilled || !selectedItem || selectedItem?.type === PreviewType.video) {
      return false;
    }
    return true;
  }

  async connectedCallback() {
    super.connectedCallback();
    this.renderDialogs = true;
    this.selectedIndex = this.initSelectedIndex();
    this.scrollCarouselItemIntoView();
    const isSignedin = await this.isSignedIn();
    this.isEntitledToCCX = await this.checkCCXEntitlement(isSignedin);
    this.userId = isSignedin ? (await window?.adobeIMS?.getProfile())?.userId : undefined;
    window.addEventListener('resize', this.resizer);
  }

  async firstUpdated() {
    this.previewWidth = this.querySelector('.preview-item')?.clientWidth;
    this.scrollCarouselItemIntoView();
  }

  updated(changedProperties: PropertyValues) {
    if (changedProperties.has('selectedIndex') && this.selectedItem) {
      this.track({
        event: AnalyticsEvent.resourceAssetPreview,
        payload: {
          id: this.resourceID,
          url: this.selectedItem.url,
          fileID: this.selectedItem.file?.id,
        },
      });
    }
  }

  resizer = () => {
    this.previewWidth = this.querySelector('.preview-item').clientWidth;
  };

  async checkCCXEntitlement(isSignedin) {
    if (!isSignedin) {
      return false;
    }
    const userType = await this.userType();
    if (userType?.startsWith('type1')) {
      return true;
    } else {
      return await isEntitled('express')
        .then((data) => data)
        .catch(() => false);
    }
  }

  isSignedIn = async () => (await window?.adobeIMS?.isSignedInUser()) || false;

  userType = async () => (await window?.adobeIMS?.getProfile())?.account_type;

  ccxTemplateType = (isCCXtemplateUrl, url) => {
    const templateType = isCCXRegularTemplateType(url) ? 'CCX Regular Template Link' : 'CCX Post Remix Template Link';
    return isCCXtemplateUrl ? templateType : 'Web Link';
  };

  handleSignIn = (isCCXtemplateUrl: boolean) => () => {
    Analytics.getInstance().emitEvent(
      AdobeAnalyticsEvent.signin,
      isCCXtemplateUrl
        ? 'Resource preview content action bar CC Express link click'
        : 'Resource file preview content action bar'
    );
    this.signUpModal(null);
  };

  setSurveyCookie = () => () => {
    if (!this.isGuest) {
      setSurveyCookieForNoCAParams(SURVEY_CONTENT_ACQUIRED_COOKIE_KEY);
    }
  };

  handlePreviewLinkClick = (meta: PreviewClickTrackingMeta) => {
    const { event, url, isCCXtemplateUrl = false, assetType = 'post', isLinkFromAssetBrowser = false } = meta;
    const metadata = [];
    if (isCCXtemplateUrl) {
      metadata.push({
        ccxLinkType: isLinkFromAssetBrowser
          ? CCX_PROJECT_LINK_ANALYTICS_IDENTIFIER
          : this.ccxTemplateType(isCCXtemplateUrl, url),
        ccxAssetType: assetType,
        isLinkFromAssetBrowser,
      });
      trackTargetEvent('edex-resource-preview-ccxlink-click');
    } else {
      trackTargetEvent('edex-resource-preview-weblink-click');
    }
    const eventName = isCCXtemplateUrl
      ? AnalyticsEvent.resourceClickCCXTemplateLink
      : AnalyticsEvent.resourceClickWebLink;
    this.track({
      event: eventName,
      payload: {
        id: this.resourceID,
        url,
        metadata,
      },
    });
    Analytics.getInstance().emitEvent(
      isCCXtemplateUrl ? AdobeAnalyticsEvent.resourceOpenCCXTemplateLink : AdobeAnalyticsEvent.resourceOpenLink,
      isCCXtemplateUrl ? 'Open link in CC Express in new tab' : 'Open link in new tab'
    );
    // keeping event names for FS same as those in AnalyticsEvent
    this.emitFSEvent(eventName, {
      resourceID: this.resourceID,
      href: url,
      ...metadata,
    });
    if (!meta.branchURL) {
      this.authGuard({ event });
    }
    this.setSurveyCookie();
  };

  emitFSEvent = (eventName: string, payload: { [key: string]: any }) => {
    FullStoryUtils.getInstance().event(eventName, payload);
  };

  scrollIntoViewByID(selector: string, options: boolean | ScrollIntoViewOptions = { behavior: 'smooth' }) {
    // Accessibility: check if user browser has requested reduced motion
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

    // if reduced motion requested, revert to normal anchor jumping
    if (!mediaQuery || mediaQuery.matches) {
      options = { behavior: 'auto' };
    }

    const cb = () => {
      const elm = this.querySelector(selector);
      if (!elm) {
        return;
      }
      elm.scrollIntoView?.(options);

      // for accessibility, whenever scroll is hijacked we should set focus on element scrolled to
      (elm as HTMLElement).focus({ preventScroll: true });
    };
    window.requestAnimationFrame(cb);
  }

  scrollCarouselItemIntoView = () => {
    const { selectedItem } = this;
    this.scrollIntoViewByID(`[data-id="${selectedItem.id}"]`, { behavior: 'smooth', block: 'nearest' });
  };

  handleChevronClick = (direction: ChevronDirection) => (event) => {
    switch (direction) {
      case ChevronDirection.left:
        this.selectedIndex = max([0, this.selectedIndex - 1]);
        break;
      case ChevronDirection.right:
        this.selectedIndex = min([this.selectedIndex + 1, this.attachments.length - 1]);
        break;
      default:
    }
    this.navigate({
      queryParams: { [QueryParam.previewItem]: this.selectedIndex },
      replaceState: true,
    });
    this.scrollCarouselItemIntoView();
  };

  handleItemDownload = (item) => () => {
    const dialog: DialogState<DownloadFileDialogPayload> = {
      id: EdexDomID.getResourceItemDialog,
      payload: { file: item.file },
    };
    if (this.isGuest) {
      this.signUpModal({ id: EdexDomID.getResourceDialog });
      return;
    }
    Analytics.getInstance().emitEvent(AdobeAnalyticsEvent.resourceDownload, 'Resource file preview download');
    trackTargetEvent('edex-resource-preview-download-click');
    this.actions.openDialog(dialog);
  };

  handleCarouselItemClick = (index, item: CarouselItem) => () => {
    if (this.selectedIndex === index) {
      return;
    }
    this.selectedIndex = index;
    const src = getNavSource();
    this.navigate({
      queryParams: { [QueryParam.previewItem]: this.selectedIndex, ...(src.length > 0 && { src }) },
      replaceState: true,
    });
    this.scrollCarouselItemIntoView();
  };

  initSelectedIndex() {
    const attachmentsCount = this.attachments?.length;
    const selectedIdx = parseInt(getQueryParam(QueryParam.previewItem), 10) || 0;
    return attachmentsCount - 1 < selectedIdx || selectedIdx <= 0 ? 0 : selectedIdx;
  }

  renderCarouselItemIcon = (item: CarouselItem) => {
    if (item.file) {
      return this.renderFileThumbnail(item);
    } else {
      switch (item.type) {
        case PreviewType.image:
          return this.renderImagePreview(item);
        case PreviewType.web:
          if (item.assetType === PreviewCCXAssetType.video) {
            return this.renderDefaultVideoPreview(item);
          }
          return this.renderLinkPreview(item, true);
        case PreviewType.video:
          return this.renderDefaultVideoPreview(item);
        default:
          return this.renderFileThumbnail(item);
      }
    }
  };

  getRedirectLink = (urn: string, assetType: string) => {
    if (NX_ENABLE_PROJECT_X_ASSET_BROWSER === 'true') {
      const projectXPageEditor = `${NX_PROJECTX_ASSET_BROWSER_URL}/webpage/design`;
      return `${projectXPageEditor}/${urn}`;
    }
    return `${NX_CCX_HOST}/sp/design/${assetType}/${urn}`;
  };

  async copyCCXProject(urn: string, assetType: string) {
    return await resourceAPI
      .post(
        `/copyProject`,
        {
          sourceUrn: urn,
          assetType,
          accessToken: fetchProfileStorage().tokenValue,
        },
        {
          timeout: COPY_PROJECT_TIMEOUT,
        }
      )
      .then(({ data }) => data)
      .catch(() => {
        // copy project operation failed, DO NOT redirect to CCX
        throw new Error('Copy project call failed');
      });
  }

  openCopyProjectProgressInNewTab = async (e, urn = null, assetType = '', url = null) => {
    const anchorElem = document.querySelector('.preview-two .open-copy-project-btn');
    if (anchorElem && anchorElem.hasAttribute('href')) {
      return;
    }
    const previewClickTrackingMeta: PreviewClickTrackingMeta = {
      event: e,
      url,
      isCCXtemplateUrl: true,
      assetType,
      isLinkFromAssetBrowser: true,
    };
    await this.handlePreviewLinkClick(previewClickTrackingMeta);
    const altWindow = window.open('', '_blank');
    altWindow.document
      .write(`<div class="loader-main" style="display: flex; flex-direction: column;align-items: center; padding-top:18vh">
    <h1>${$t(I18nKey.copyProjectDialogHeading)}</h1>
    <p style="color: grey; font-weight: 100">${$t(I18nKey.copyProjectDialogSubHeading)}</p>
    <img style="margin: auto; top: 0; left: 0;bottom: 0; right: 0;" src="${NX_CDN_HOST}/assets/v3/images/ccx_copy_loading.gif"/>`);

    let copiedUrn = null;
    try {
      copiedUrn = await this.copyCCXProject(urn, assetType);
    } catch (err) {
      altWindow.close();
      return;
    }
    if (!copiedUrn) {
      return;
    }

    const redirectLink = this.getRedirectLink(copiedUrn, assetType);
    altWindow.location.href = redirectLink;
  };

  renderCarouselItem = (item: CarouselItem, index) => {
    const { selectedIndex } = this;
    const { components, title } = item;
    const labelComponent = components && components.length > 0 ? RESOURCE_COMPONENT_LABELS[components[0]] : title;
    const isSelected = index === selectedIndex;
    return html`
      <div
        class="carousel-item-two"
        data-id=${item.id}
        @click=${this.handleCarouselItemClick(index, item)}
        tabindex="0"
      >
        <div
          class="carousel-thumbnail ${classMap({
            selected: isSelected,
          })}"
        >
          ${this.renderCarouselItemIcon(item)}
        </div>
        <div
          class="carousel-item-label ${classMap({
            selected: isSelected,
          })}"
        >
          <span>${$t(labelComponent)}</span>
        </div>
      </div>
    `;
  };

  renderLinkUrlPreview = () => {
    return html`
      <img src="${getAssetURL('default_weblink_preview.png')}" alt="ccx-template-icon" />
    `;
  };

  renderCCXTemplateIcon = (isCCXtemplateUrl: boolean, hasMetadata: boolean) => {
    return isCCXtemplateUrl && hasMetadata
      ? html`
          <img src="${getAssetURL('ccx_template_icon.svg')}" alt="ccx-template-icon" width="104" height="50" />
        `
      : html`
          <img src="${getAssetURL('default_weblink_preview.svg')}" alt="ccx-template-icon" />
        `;
  };

  renderFileThumbnail(item: CarouselItem = this.selectedItem) {
    const { file } = item;
    return file
      ? html`
          <edex-file-thumbnail .file=${item.file}></edex-file-thumbnail>
        `
      : null;
  }

  renderPreviewIcon = (item: CarouselItem) => {
    const { icon, title, image } = item;
    return html`
      <img class="preview-icon  ${classMap({ image: !!image })}" responsive src="${image || icon}" alt="${title}" />
    `;
  };

  renderPdfPreview = () => {
    const { url } = this;
    return html`
      <object data=${`${url}#toolbar=0`} type="application/pdf">${this.renderFileThumbnail()}</object>
    `;
  };

  renderImagePreview = (item: CarouselItem = this.selectedItem) => {
    return html`
      <div class="default-document-preview">
        <object class="preview-thumbnail" data="${item.url}" type="image/png">${this.renderPreviewIcon(item)}</object>
      </div>
    `;
  };

  renderDocumentPreview = () => {
    return this.renderFileThumbnail();
  };

  renderVideoPreview = () => {
    const { selectedItem } = this;
    const { largeThumbnail, url, isProjectXRemixLink } = selectedItem;
    const playerHeight = min([this.clientHeight, (9 / 16) * this.previewWidth]);
    return html`
      <iframe
        class="video-preview"
        src="${isProjectXRemixLink && largeThumbnail ? largeThumbnail : url}"
        sandbox="allow-scripts allow-same-origin allow-popups"
        allow="fullscreen"
        style="height:${playerHeight}px"
      ></iframe>
    `;
  };

  renderTextPreview = () => {
    const { url } = this;
    return html`
      <iframe src="${url}" sandbox></iframe>
    `;
  };

  renderLinkPreview = (item: CarouselItem = this.selectedItem, isThumb = false) => {
    const {
      url,
      isLinkFromAssetBrowser,
      components,
      hasMetadata,
      largeThumbnail,
      isProjectXRemixLink,
      isLinkFromProjectXAssetBrowser,
      gneissAlias,
    } = item;
    let image = item?.image;
    const ccxValiditySchema = {
      url,
      components,
      isLinkFromAssetBrowser,
      isProjectXRemixLink,
      isLinkFromProjectXAssetBrowser,
    };
    const isCCXtemplateUrl = isCCXTemplateOrProjectUrl(ccxValiditySchema);
    if (isProjectXRemixLink) {
      image = largeThumbnail;
    }
    if (isLinkFromProjectXAssetBrowser && isEmpty(image)) {
      image = `${PAGE_URL}/${gneissAlias}/resources?image_assets=false&size=2000`;
    }
    return html`
      ${isThumb && !image
        ? this.renderCCXTemplateIcon(isCCXtemplateUrl, hasMetadata)
        : html`
            <div
              class="${classMap({
                'default-document-preview': !!image,
                link: !image,
              })}"
            >
              ${image
                ? html`
                    <object class="preview-thumbnail" data="${image}" type="image/png">
                      ${this.renderPreviewIcon(item)}
                    </object>
                  `
                : this.renderLinkUrlPreview()}
            </div>
          `}
    `;
  };

  renderNoPreviewAvailable = () => {
    const {
      selectedItem: { title, icon },
    } = this;
    return html`
      <sp-illustrated-message
        heading="${$t(I18nKey.noPreviewAvailable)}"
        description="${$t(I18nKey.cannotBePreviewed, { title })}"
        class="content-message"
      >
        <img src="${icon}" class="no-preview" alt="${$t(I18nKey.noPreviewAvailable)}" />
      </sp-illustrated-message>
    `;
  };

  renderVideoPlayIcon(hasImage) {
    return html`
        <div class="cover ${classMap({ image: hasImage })}">
          <sp-icon class="decorator_action" size="xxl">
            <sp-icon-play-circle></sp-icon-play-circle>
          </sp-icon>
        </div>
      </div>
    `;
  }

  renderDefaultVideoPreview = (item: CarouselItem = this.selectedItem) => {
    const previewURL = item.image;
    const hasImage = !!previewURL;
    return html`
      <div class="default-document-preview">
        ${previewURL
          ? html`
              <img class="preview-thumbnail" src="${previewURL}" alt="" style="height: 41px;" />
              <!-- ${this.renderVideoPlayIcon(hasImage)} -->
            `
          : this.renderVideoPlayIcon(hasImage)}
      </div>
    `;
  };

  renderPreview = () => {
    const { selectedItem, selfIsFulfilled } = this;
    if (!(selectedItem && selfIsFulfilled)) {
      return null;
    }

    switch (selectedItem.type) {
      case PreviewType.pdf:
        return this.renderPdfPreview();
      case PreviewType.image:
        return this.renderImagePreview();
      case PreviewType.document:
        return this.renderDocumentPreview();
      case PreviewType.video:
        return this.renderVideoPreview();
      case PreviewType.text:
        return this.renderTextPreview();
      case PreviewType.web:
        if (selectedItem.assetType === PreviewCCXAssetType.video) {
          return this.renderVideoPreview();
        }
        return this.renderLinkPreview();
      default:
        return this.renderNoPreviewAvailable();
    }
  };

  renderGuestLinkActionBar = (isCCXtemplateUrl) => {
    return html`
      <sp-action-button @click=${this.handleSignIn(isCCXtemplateUrl)}>
        <sp-icon slot="icon">
          ${isCCXtemplateUrl
            ? html`
                <img src="${getAssetURL('express-logo.svg')}" alt="express-logo" width="104" height="50" />
              `
            : html`
                <sp-icon-link></sp-icon-link>
              `}
        </sp-icon>
        <span>${isCCXtemplateUrl ? $t(I18nKey.resourcePreviewOpenCTA) : $t(I18nKey.resourcePreviewWebpageCTA)}</span>
      </sp-action-button>
    `;
  };

  renderLinkActionButton = () => {
    const { isEntitledToCCX, selectedItem, isGuest } = this;
    const {
      url,
      components,
      branchURL,
      isLinkFromAssetBrowser,
      isLinkFromProjectXAssetBrowser,
      remixable,
      isProjectXRemixLink,
    } = selectedItem;
    let mobileBrowser = false;
    if (window.matchMedia('(max-width: 600px)').matches) {
      mobileBrowser = true;
    }
    const ccxValiditySchema = {
      url,
      components,
      isLinkFromAssetBrowser,
      isProjectXRemixLink,
      isLinkFromProjectXAssetBrowser,
    };
    const isCCXtemplateUrl = isCCXTemplateOrProjectUrl(ccxValiditySchema);
    const userIsOwner = this.userId === selectedItem.ownerUserId;
    const branchUrlOrRemixLink = branchURL || selectedItem.url;
    /* Remove after Project-X GA : CCEDU-6799
      branch and remixable -> branch/main URL
      branch and non-remix -> branch/main URL
      non-branch and non-remix -> branch/main URL
      non-branch and remix -> redirect URL
    */
    const remixableRegularLink =
      !branchURL && remixable && !isProjectXRemixLink
        ? selectedItem.url.replace('/post', CCX_POST_REMIX_DIRECT_LINK)
        : branchUrlOrRemixLink;
    // Check added for branchURL as part of CCEDU-6934
    // Add ProjectX check here in case this behaviour is not required for X Remix links
    if (isGuest && !branchURL) {
      return this.renderGuestLinkActionBar(isCCXtemplateUrl);
    }
    const updatedURL =
      isCCXtemplateUrl && !isProjectXRemixLink
        ? !isLinkFromAssetBrowser
          ? remixableRegularLink
          : this.userId === selectedItem.ownerUserId
          ? this.getRedirectLink(selectedItem.urn, selectedItem.assetType)
          : selectedItem.url
        : selectedItem.url;
    return isCCXtemplateUrl && isLinkFromAssetBrowser && !userIsOwner
      ? mobileBrowser && NX_RAPI_FLAG === 'true'
        ? html`
            <edex-copy-mobile-modal>
              <overlay-trigger type="modal" placement="none" offset="6">
                <sp-action-button
                  class="express-btn open-copy-project-btn fs-open-template-btn"
                  slot="trigger"
                  .disabled=${!isEntitledToCCX}
                >
                  <sp-icon slot="icon">
                    <img src="${getAssetURL('express-logo.svg')}" alt="express-logo" width="104" height="50" />
                  </sp-icon>
                  <span>${$t(I18nKey.resourcePreviewOpenCTA)}</span>
                </sp-action-button>
                // eslint-disable-next-line prettier/prettier
                <sp-dialog-wrapper
                  class="copy-dialog-mobile"
                  size="s"
                  no-divider
                  slot="click-content"
                  dismissable
                  underlay
                >
                  <div class="copy-dialog-mobile-content">
                    <h2>${$t(I18nKey.copyMobileModalTitle)}</h2>
                    <img src="${getAssetURL('copy_modal_icon.svg')}" alt="copy_modal_icon" />
                    </div>
                    <p>${$t(I18nKey.copyMobileModalBody)}</p>
                  </div>
                </sp-dialog-wrapper>
              </overlay-trigger>
            </edex-copy-mobile-modal>
          `
        : html`
            <sp-action-button
              class="express-btn open-copy-project-btn fs-open-template-btn"
              @click=${(event) =>
                this.openCopyProjectProgressInNewTab(
                  event,
                  selectedItem?.urn,
                  selectedItem.assetType,
                  selectedItem.url
                )}
              target="_blank"
              .disabled=${!isEntitledToCCX}
            >
              <sp-icon slot="icon">
                <img src="${getAssetURL('express-logo.svg')}" alt="express-logo" width="104" height="50" />
              </sp-icon>
              <span>${$t(I18nKey.resourcePreviewOpenCTA)}</span>
            </sp-action-button>
          `
      : html`
          <sp-action-button
            href="${updatedURL}"
            target="_blank"
            @click="${(event) =>
              this.handlePreviewLinkClick({
                event,
                url,
                isCCXtemplateUrl,
                assetType: selectedItem.assetType,
                isLinkFromAssetBrowser,
                branchURL,
              })}"
            class="fs-open-weblink-btn ${classMap({ 'express-btn': isCCXtemplateUrl })}"
          >
            <sp-icon slot="icon">
              ${isCCXtemplateUrl || isProjectXRemixLink
                ? html`
                    <img src="${getAssetURL('express-logo.svg')}" alt="express-logo" width="104" height="50" />
                  `
                : html`
                    <sp-icon-link></sp-icon-link>
                  `}
            </sp-icon>
            <span>
              ${isCCXtemplateUrl || isProjectXRemixLink
                ? $t(I18nKey.resourcePreviewOpenCTA)
                : $t(I18nKey.resourcePreviewWebpageCTA)}
            </span>
          </sp-action-button>
        `;
  };

  renderActionButton = () => {
    const { selectedItem, showActionButton } = this;
    if (!showActionButton) {
      return null;
    }

    const canDownload = selectedItem.file?.id;
    return canDownload
      ? html`
          <sp-action-button @click=${this.handleItemDownload(selectedItem)}>
            <sp-icon slot="icon"><sp-icon-download /></sp-icon>
            <span>${$t(I18nKey.resourcePreviewActionbarDownload)}</span>
          </sp-action-button>
        `
      : this.renderLinkActionButton();
  };

  renderDownloadItemDialog = () => {
    return this.renderDownloadDialog({
      id: EdexDomID.getResourceItemDialog,
      header: I18nKey.downloadFile,
    });
  };

  renderCCXNonEntitlementToast() {
    const { isEntitledToCCX, selectedItem, isGuest } = this;
    const {
      url,
      isLinkFromAssetBrowser,
      components,
      isProjectXRemixLink,
      isLinkFromProjectXAssetBrowser,
    } = selectedItem;
    const ccxValiditySchema = {
      url,
      components,
      isLinkFromAssetBrowser,
      isProjectXRemixLink,
      isLinkFromProjectXAssetBrowser,
    };
    const isCCXtemplateUrl = isCCXTemplateOrProjectUrl(ccxValiditySchema);

    if (!isGuest && isCCXtemplateUrl && selectedItem.isLinkFromAssetBrowser && !isEntitledToCCX) {
      return html`
        <sp-toast open variant="negative">${$t(I18nKey.attachmentPreviewCCXEntitlementErrorText)}</sp-toast>
      `;
    } else {
      return null;
    }
  }

  render() {
    const { selectedIndex, attachments } = this;
    return html`
      <div class="preview-two">
        <div class="carousel-preview">${attachments.map(this.renderCarouselItem)}</div>
        <div class="preview-item">
          <div class="preview-no-ccx-entitlement-toast">${this.renderCCXNonEntitlementToast()}</div>
          <div class="arrow left" @click=${this.handleChevronClick(ChevronDirection.left)}>
            <sp-icon
              class="${classMap({
                disabled: selectedIndex === 0,
                'color-disabled': selectedIndex === 0,
              })}"
            >
              <sp-icon-chevron-left />
            </sp-icon>
          </div>
          ${this.renderPreview()} ${this.renderActionButton()}
          <div class="arrow right" @click=${this.handleChevronClick(ChevronDirection.right)}>
            <sp-icon
              class="${classMap({
                disabled: selectedIndex === attachments.length - 1,
                'color-disabled': selectedIndex === attachments.length - 1,
              })}"
            >
              <sp-icon-chevron-right />
            </sp-icon>
          </div>
        </div>
        ${this.renderDownloadItemDialog()} ${this.renderSignInConfirmationDialog()}
      </div>
    `;
  }
}
